import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  useTheme,
  Container,
} from "@mui/material";
import {
  AttachMoney,
  Security,
  Bolt,
  CheckCircle,
  Close,
  AutoAwesome,
  Sms,
} from "@mui/icons-material";
import { MdCurrencyRupee, MdWhatsapp } from "react-icons/md";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import BackBtn from "../../widgets/BackBtn";

const RechargeComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const theme = useTheme();

  const isAI = id === "ai";
  const isRCS = id === "rcs";

  const config = isAI
    ? {
        title: "AI Power Recharge",
        subtitle: "Fuel Your WhatsApp Business Automation (Minimum ₹2000)",
        description:
          "Add credits to power your AI-driven WhatsApp automation. Each message, response, and automation uses credits from your balance.",
        successMessage:
          "Your request for ₹{amount} has been sent to our admin team. Credits will be added to your account shortly.",
        icon1: "WhatsApp",
        icon2: "AutoAwesome",
        poweredLabel: "AI Powered",
      }
    : isRCS
    ? {
        title: "RCS Power Recharge",
        subtitle: "Fuel Your RCS Business Messaging (Minimum ₹2000)",
        description:
          "Add credits to power your RCS messaging services. Each message and response uses credits from your balance.",
        successMessage:
          "Your request for ₹{amount} has been sent to our admin team. RCS credits will be added to your account shortly.",
        icon1: "Sms",
        icon2: "AutoAwesome",
        poweredLabel: "RCS Powered",
      }
    : {
        title: "Power Recharge",
        subtitle: "Fuel Your Services (Minimum ₹2000)",
        description:
          "Add credits to power your services. Each usage consumes credits from your balance.",
        successMessage:
          "Your request for ₹{amount} has been sent to our admin team. Credits will be added to your account shortly.",
        icon1: "Bolt",
        icon2: "AutoAwesome",
        poweredLabel: "Powered",
      };

  const MIN_RECHARGE = 2000;
  const predefinedAmounts = [2000, 5000, 10000, 20000, 50000];

  const baseAmount = Number(amount) || 0;
  const isValidAmount = baseAmount >= MIN_RECHARGE;

  const handleRecharge = async () => {
    if (!amount || isNaN(amount) || !isValidAmount) {
      setError(`Please enter a valid amount (Minimum ₹${MIN_RECHARGE})`);
      return;
    }
    setError("");
    setOpenConfirm(true);
  };

  const confirmRecharge = async () => {
    setLoading(true);
    setError("");
    setOpenConfirm(false);

    try {
      // Simulate API call to backend

      const response = await axios.post(
        `${API_ENDPOINT}/api/request-credit`,
        { type: "recharge request", amount, category: id },
        getHeader()
      );
      toast.success(response.data.message);
      setTimeout(() => {
        navigate(-1);
      }, 100);

      setSuccess(true);
      setAmount("");
    } catch (err) {
      setError("Failed to process recharge request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePredefinedAmount = (value) => {
    setAmount(value.toString());
    setError("");
  };

  const resetState = () => {
    setSuccess(false);
    setError("");
    setAmount("");
  };

  const getIcon = (name) => {
    const icons = {
      WhatsApp: <MdWhatsapp sx={{ fontSize: 40, mr: 2 }} />,
      Sms: <Sms sx={{ fontSize: 40, mr: 2 }} />,
      Bolt: <Bolt sx={{ fontSize: 40, mr: 2 }} />,
    };
    return icons[name] || <Bolt sx={{ fontSize: 40, mr: 2 }} />;
  };

  const successText = config.successMessage.replace("{amount}", amount);

  return (
    <Container sx={{ mt: 3 }}>
      <BackBtn />
      <Box sx={{ maxWidth: 700, margin: "0 auto", px: 3 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: "white",
            p: 4,
            mb: 3,
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            {getIcon(config.icon1)}
            <AutoAwesome sx={{ fontSize: 40 }} />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            {config.title}
          </Typography>

          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
            {config.subtitle}
          </Typography>

          <Chip
            icon={<Security />}
            label="Secure Instant Processing"
            variant="outlined"
            sx={{
              color: "white",
              borderColor: "white",
              background: "rgba(255,255,255,0.1)",
              mb: 1,
            }}
          />
        </Paper>

        {/* Main Recharge Card */}
        <Card elevation={3} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {success ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CheckCircle
                  sx={{ fontSize: 64, color: "success.main", mb: 2 }}
                />
                <Typography variant="h5" gutterBottom color="success.main">
                  Recharge Request Sent!
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {successText}
                </Typography>
                <Button
                  variant="contained"
                  onClick={resetState}
                  startIcon={<Bolt />}
                >
                  Request Another Recharge
                </Button>
              </Box>
            ) : (
              <>
                {/* Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Bolt sx={{ mr: 1, color: "warning.main" }} />
                    Why Recharge?
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {config.description}
                  </Typography>

                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Chip
                        icon={<CheckCircle />}
                        label="Instant Processing"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Chip
                        icon={<Security />}
                        label="Secure Payment"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Amount Input Section */}
                <Typography variant="h6" gutterBottom>
                  Enter Recharge Amount
                </Typography>

                {/* Predefined Amounts */}
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {predefinedAmounts.map((value) => (
                    <Grid item key={value}>
                      <Chip
                        label={`₹${value}`}
                        onClick={() => handlePredefinedAmount(value)}
                        variant={
                          amount === value.toString() ? "filled" : "outlined"
                        }
                        color={
                          amount === value.toString() ? "primary" : "default"
                        }
                        clickable
                      />
                    </Grid>
                  ))}
                </Grid>

                {/* Custom Amount Input */}
                <TextField
                  fullWidth
                  label="Custom Amount (₹)"
                  variant="outlined"
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    if (
                      e.target.value &&
                      Number(e.target.value) < MIN_RECHARGE
                    ) {
                      setError(`Minimum recharge amount is ₹${MIN_RECHARGE}`);
                    } else {
                      setError("");
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <MdCurrencyRupee
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                    ),
                  }}
                  placeholder="Enter amount in rupees"
                  error={!!error}
                  helperText={error || `Minimum ₹${MIN_RECHARGE}`}
                  sx={{ mb: 3 }}
                />

                {/* Action Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleRecharge}
                  disabled={loading || !amount || !isValidAmount}
                  startIcon={<Bolt />}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }}
                >
                  {loading ? "Processing..." : "Request Recharge"}
                </Button>

                {/* Additional Info */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block", textAlign: "center" }}
                >
                  Your request will be processed by our admin team. You'll
                  receive confirmation once credits are added.
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* Features Section */}
        {!success && !loading && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={1}
                sx={{ p: 2, textAlign: "center", borderRadius: 2 }}
              >
                <Bolt color="primary" sx={{ mb: 1 }} />
                <Typography variant="body2" fontWeight="bold">
                  Instant Processing
                </Typography>
                <Typography variant="caption">
                  Quick credit allocation
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={1}
                sx={{ p: 2, textAlign: "center", borderRadius: 2 }}
              >
                <Security color="primary" sx={{ mb: 1 }} />
                <Typography variant="body2" fontWeight="bold">
                  Secure
                </Typography>
                <Typography variant="caption">
                  Safe transaction process
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={1}
                sx={{ p: 2, textAlign: "center", borderRadius: 2 }}
              >
                <AutoAwesome color="primary" sx={{ mb: 1 }} />
                <Typography variant="body2" fontWeight="bold">
                  {config.poweredLabel}
                </Typography>
                <Typography variant="caption">
                  {isAI
                    ? "Smart automation"
                    : isRCS
                    ? "Advanced messaging"
                    : "Enhanced services"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Confirmation Dialog */}
        <Dialog
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Confirm Recharge Request</Typography>
            <IconButton onClick={() => setOpenConfirm(false)}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are requesting a recharge of:
            </Typography>
            <Typography
              variant="h4"
              color="primary"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              ₹{amount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This request will be sent to our admin team for processing.
              Credits will be added to your account upon approval.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenConfirm(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={confirmRecharge}
              variant="contained"
              startIcon={<Bolt />}
            >
              Confirm Request
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default RechargeComponent;
